import React from 'react';
import { useTranslation } from 'react-i18next';
import { CarCard } from '@perxis-auto/ui-kit/lib/components/CarCard';
import { Col, Row } from '@perxis-auto/ui-kit/lib/components/Grid';

import PairBlockButton from '@/components/PairBlockButton';
import PAGES from '@/constants/pages';
import useComparison from '@/features/comparison/hooks/useCompare';
import useFavorites from '@/features/favorites/hooks/useFavorites';
import { useInjection } from '@/services/hooks';
import { INavigationService } from '@/services/NavigationService';
import ServiceTypes from '@/services/ServiceTypes';

import { CardButtonsProps } from './CardActions.types';

function CardActions({ data, isLoading = false }: CardButtonsProps) {
  const { t } = useTranslation();
  const navigation = useInjection<INavigationService>(ServiceTypes.Navigation);

  const [isCompared, toggleComparison] = useComparison(data?.id);
  const [isFavorite, toggleFavorites] = useFavorites(data?.id);

  const toggleFavoritesText = t(
    isFavorite ? 'cardActions.removeFavorites' : 'cardActions.addFavorites',
  );
  const toggleComparisonText = t(
    isCompared ? 'cardActions.removeComparison' : 'cardActions.addComparison',
  );

  const handleComparisonButtonClick = () => {
    if (!isCompared) {
      toggleComparison();
    }
  };

  const handleFavoritesButtonClick = () => {
    if (!isFavorite) {
      toggleFavorites();
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <Row gutter={[18, 0]}>
      <Col flex="none">
        <CarCard.ButtonTooltip
          isActive={isCompared}
          icon="compare"
          onClick={handleComparisonButtonClick}
          hoverableTooltipContent={toggleComparisonText}
          clickableTooltipContent={() => (
            <PairBlockButton
              icon={[isCompared ? 'minus' : 'plus', 'forward']}
              onClick={[toggleComparison, () => navigation.push(PAGES.Comparison)]}
            >
              {[toggleComparisonText, t('cardActions.redirectComparison')]}
            </PairBlockButton>
          )}
        >
          {t('cardActions.comparison')}
        </CarCard.ButtonTooltip>
      </Col>
      <Col flex="none">
        <CarCard.ButtonTooltip
          isActive={isFavorite}
          icon="favorites"
          onClick={handleFavoritesButtonClick}
          hoverableTooltipContent={toggleFavoritesText}
          clickableTooltipContent={() => (
            <PairBlockButton
              icon={[isFavorite ? 'minus' : 'plus', 'forward']}
              onClick={[toggleFavorites, () => navigation.push(PAGES.Favorites)]}
            >
              {[toggleFavoritesText, t('cardActions.redirectFavorites')]}
            </PairBlockButton>
          )}
        >
          {t('cardActions.favorites')}
        </CarCard.ButtonTooltip>
      </Col>
    </Row>
  );
}

export default CardActions;
